import type { FC, ReactNode } from 'react'

import { Instagram, Mail, Moon } from 'lucide-react'
import { useSiteContent, useTextKeys } from '@/lib/site-content'
import LinkButton from '@/components/controls/link-button'
import CmsText from '@/components/generals/cms-text'
import { FadeIn } from '@/components/motion/fade-in'
import FloatingBlob from '@/components/motion/floating-blob'

// Shown in place of the order form while orders are closed (NEXT_PUBLIC_ORDERS_LIVE = "false").
const OrderClosed: FC = (): ReactNode => {
    const { settings } = useSiteContent()
    const paragraphKeys = useTextKeys('order.closed.paragraphs.')

    return (
        <FadeIn>
            <div className='relative mx-auto max-w-2xl'>
                <FloatingBlob
                    className='-left-24 -top-16 h-64 w-64 text-brand-yellow-soft/50'
                    amplitude={12}
                    duration={11}
                />

                <FloatingBlob
                    className='-bottom-20 -right-28 h-72 w-72 text-brand-blob-orange/45'
                    amplitude={16}
                    duration={13}
                    delay={1.2}
                />

                <div className='relative overflow-hidden rounded-card bg-white/85 px-7 py-12 text-center shadow-soft md:px-12 md:py-16'>
                    <span className='inline-flex items-center gap-2 rounded-pill bg-brand-yellow-soft/70 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-brand-red'>
                        <Moon aria-hidden className='h-3.5 w-3.5' />
                        <CmsText textKey='order.closed.badge' />
                    </span>

                    <CmsText
                        as='h2'
                        textKey='order.closed.title'
                        className='mt-6 font-display text-4xl font-extrabold text-neutral-900 md:text-5xl'
                    />

                    <div className='mx-auto mt-5 max-w-xl space-y-4 text-base leading-relaxed text-neutral-700'>
                        {paragraphKeys.map(key => (
                            <CmsText key={key} as='p' textKey={key} />
                        ))}
                    </div>

                    <div className='mt-9 flex flex-wrap items-center justify-center gap-3'>
                        <LinkButton
                            href={settings.instagram_url ?? '#'}
                            target='_blank'
                            rel='noopener noreferrer'
                            variant='red'
                            size='lg'
                        >
                            <Instagram aria-hidden className='h-4 w-4' />
                            <CmsText textKey='order.closed.followCta' />
                        </LinkButton>

                        <LinkButton href={`mailto:${settings.contact_email}`} variant='yellow' size='lg'>
                            <Mail aria-hidden className='h-4 w-4' />
                            <CmsText textKey='order.closed.contactCta' />
                        </LinkButton>
                    </div>
                </div>
            </div>
        </FadeIn>
    )
}

export default OrderClosed
